import { TopBar, Icon, Typography } from '@equinor/eds-core-react'
import { useNavigate } from 'react-router-dom'
import { useCallback, useRef, useState } from 'react'
import { IconStyle, ClickableTitleStyle, Logo } from '@/components/styles/HeadersStyles'
import UserAccountMenu from '@/components/header/UserAccountMenu'
import PlantSelector from '@/components/header/PlantSelector'
import { useCurrentPlant } from '@/core/hooks-and-services/useCurrentPlant'
import styled from 'styled-components'

const { Actions, Header } = TopBar

function Headers() {
    const userRef = useRef<HTMLDivElement>(null)
    const { currentPlant } = useCurrentPlant()

    // State to manage whether the user menu is open
    const [isUserMenuOpen, setUserMenuOpen] = useState(false)

    // Function to show user menu
    const showUserMenu = () => {
        setUserMenuOpen(true)
    }

    // Function to hide user menu
    const hideUserMenu = () => {
        setUserMenuOpen(false)
    }

    //const userRef = useRef<HTMLButtonElement>(null)

    const navigate = useNavigate()
    const handleHomeClick = () => {
        navigate('/')
    }

    const renderUserMenu = useCallback(
        () => <UserAccountMenu onClose={hideUserMenu} anchor={userRef.current} />,
        [hideUserMenu]
    )

    return (
        <TopBarStyled>
            <Header>
                <ClickableTitleStyle onClick={handleHomeClick}>
                    <ResponsiveLogo />
                    <TitleColumn>
                        <StyledTypography>Industrial Internet of Things</StyledTypography>
                        <PlantSubtitle>{currentPlant?.description ?? 'All plants'}</PlantSubtitle>
                    </TitleColumn>
                </ClickableTitleStyle>
            </Header>
            <CenterSlot>
                <PlantSelector />
            </CenterSlot>
            <ActionsStyled>
                <TitleStyle>
                    <IconStyle onClick={showUserMenu} style={{ cursor: 'pointer' }}>
                        <StyledTypography style={{ fontSize: '14px', padding: '0px' }}>
                            Profile & settings
                        </StyledTypography>
                        <div ref={userRef}>
                            <Icon name="accountCircle" size={24} title="user" />
                        </div>
                    </IconStyle>
                </TitleStyle>
                <TitleStyle
                    onClick={() =>
                        window.open(
                            'https://forms.office.com/Pages/ResponsePage.aspx?id=NaKkOuK21UiRlX_PBbRZsD3jOHqSMdpJknKjfcoA3wZUOUZDMUpWWDBSSEE1QUkwNUtHUEVBWTNZMyQlQCN0PWcu',
                            '_blank'
                        )
                    }
                    style={{ cursor: 'pointer' }}
                >
                    <IconStyle>
                        <StyledTypography style={{ fontSize: '14px', padding: '0px' }}>Feedback</StyledTypography>
                        <Icon name="thumbsUpDown" size={24} title="feedback" />
                    </IconStyle>
                </TitleStyle>
            </ActionsStyled>
            {isUserMenuOpen && renderUserMenu()} {/* Render the menu only if it's open */}
        </TopBarStyled>
    )
}

const ResponsiveLogo = styled(Logo)`
    width: 32px;
    height: 32px;
    content: url('/Equinor_Symbol_Energy_White.svg');
`

const StyledTypography = styled(Typography)`
    gap: 8px;
    padding: 16px;
    text-decoration: none;
    background: none;
    border: none;
    font-size: 20px;
    font-style: normal;
    font-weight: 400;
    line-height: 16px;
    color: white;
`

const TitleColumn = styled.div`
    display: flex;
    flex-direction: column;

    /* Compact title padding so title + subtitle fit inside the topbar height. */
    & > *:first-child {
        padding-top: 8px;
        padding-bottom: 0;
    }
`

const PlantSubtitle = styled(Typography)`
    padding: 0 4px 8px 16px;
    margin-top: 2px;
    font-size: 14px;
    line-height: 16px;
    font-weight: 400;
    color: #cae4e7;
`

const TopBarStyled = styled(TopBar)`
    flex: 0 0 0 3rem;
    align-items: center;
    width: 100vw;
    justify-content: center;
    background: #007079;
    height: auto;
    border-bottom: none;
    box-shadow: none;
    position: relative;

    @media (max-width: 1024px) {
        display: flex;
    }
`

const ActionsStyled = styled(Actions)`
    align-items: center;
    display: flex;
    flex-direction: row-reverse;

    > :not(:last-child) {
        margin-left: 16px;
    }

    @media (max-width: 1024px) {
        display: none;
    }
`

const CenterSlot = styled.div`
    /* Absolute-centered so the plant selector stays put regardless of the title width. */
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 16px;

    @media (max-width: 1024px) {
        display: none;
    }
`

export const TitleStyle = styled.div`
    display: flex;
    align-items: center;
    flex-direction: row-reverse;
    > * {
        margin-right: 0.3rem;
    }
    cursor: pointer;
    display: flex;
    align-items: center;
    transition: opacity 0.2s ease;

    &:hover {
        opacity: 0.8;
    }

    &:active {
        transform: scale(0.98);
    }
`

export default Headers
